import { Feature, FeatureModel } from '@/models/feature.model';
import { storeToRefs } from 'pinia';
import { useFeaturesStore } from '../stores/features';
import { CrudService } from './crud.service';

export class FeaturesService {
  public static readonly priceFeatureEnabled = import.meta.env.VITE_APP_priceFeatureEnabled === 'true';
  public static readonly selectableFieldsEnabled = import.meta.env.VITE_APP_selectableFieldsEnabled === 'true';
  public static readonly enableSelfSellIn = import.meta.env.VITE_APP_enableSelfSellIn === 'true';
  public static readonly orderTemplateDownloadEnabled =
    import.meta.env.VITE_APP_downloadOrderTemplateEnabled === 'true';
  public static readonly mcaEnabled = import.meta.env.VITE_APP_mcaEnabled === 'true';

  /**
   * PLACEHOLDER — dev/QA tool only. Not for production use.
   *
   * When true, shows a V1/V2 toggle in the footer so developers and testers can switch
   * web versions without clearing cookies manually. Must be `false` in all production
   * and user-facing environments.
   *
   * To remove this feature entirely, delete:
   *   - This flag and the lines below
   *   - The `version-switcher` <li> block in apps/web/src/layouts/MainFooter.vue
   *   - The `isWebVersionSwitcherEnabled`, `currentWebVersion`, `isSwitchingVersion`,
   *     `readWebVersionCookie`, and `onVersionChange` additions in that same file
   *   - The `.version-switcher` SCSS block in that same file
   *   - The corresponding entry in apps/web/.env.example
   */
  public static readonly webVersionSwitcherEnabled = import.meta.env.VITE_APP_webVersionSwitcherEnabled === 'true';

  private crudService: CrudService<FeatureModel>;
  private errorMode!: boolean;

  constructor() {
    this.crudService = new CrudService<FeatureModel>('features');
  }

  public async hasFeature(feature: Feature, accountId: string): Promise<boolean> {
    // if fetching of features fails, fetch them again next time, but for now, return false.
    const features = await this.getFeatures(accountId);

    return !this.errorMode && features?.[feature];
  }

  private async getFeatures(accountId: string): Promise<FeatureModel> {
    const { features } = storeToRefs(useFeaturesStore());
    const { addAcountFeatures } = useFeaturesStore();

    if (!features.value[accountId] && accountId) {
      const result = await this.crudService.do('get', `?accountId=${accountId}`);
      if (result.isValid() && result.model) {
        this.errorMode = false;
        addAcountFeatures({ [accountId]: result.model });
      } else {
        this.errorMode = true;
      }
    }

    return features.value[accountId];
  }
}
